import {buildBookingLink, useParsedBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {useSendLoginToken} from "@page-containers/confirm-identity/components/useSendLoginToken";
import {Trans, useTranslation} from "@i18n/client";
import React from "react";
import {getFlowId} from "src/utils/getFlowId";

import {I18nNamespace} from "@i18n";
import {AsyncButton, ButtonSize} from "../../Button";
import Icon from "../../Icon";
import Heading from "../Heading";
import {useUserByEmailQuery} from "./graphql/__generated__/user-by-email.type";

const Circle = () => {
  return (
    <div className="bg-green-200 rounded-full flex items-center justify-center h-16 w-16">
      <Icon icon="calendar-appt" className="text-3xl leading-none" />
    </div>
  );
};

const TokenSent = ({email}: {email: string}) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);

  const userQuery = useUserByEmailQuery({
    variables: {
      email,
    },
  });

  const user = userQuery.data?.user?.user;
  const unauthedPatient = user?.adultPatient;
  const anonymizedEmail = user?.anonymizedEmail;

  const query = useParsedBookingQuery();
  const redirectUrl = buildBookingLink({
    ...query,
    confirmationEmailSent: undefined,
  });
  const {sendEmail, sendSms} = useSendLoginToken({
    email,
    flowId: getFlowId(),
    redirectUrl,
    isResend: false,
    patientId: unauthedPatient?.id || "",
  });

  return (
    <div className="flex flex-col items-center gap-12">
      <div className="flex flex-col items-center gap-3">
        <Circle />
        <Heading className="text-center">
          <Trans ns="web-scheduling">
            Welcome back! <br />
            You're almost finished...
          </Trans>
        </Heading>
        <p className="body-md text-center">
          {anonymizedEmail
            ? t(
                "To finish booking, please click the secure confirmation link we sent to {{emailAddress}}.",
                {
                  emailAddress: anonymizedEmail,
                  ns: "web-scheduling",
                },
              )
            : t(
                "To finish booking, please click the secure confirmation link we sent to your email address.",
                {
                  ns: "web-scheduling",
                },
              )}
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <p className="body-md text-center">
          {t("Didn't receive the link? Check your spam folder.", {
            ns: "web-scheduling",
          })}
        </p>
        <div className="flex gap-2">
          <AsyncButton
            label={t("Email me a new link", {ns: "web-scheduling"})}
            loadingLabel={t("Sending email", {ns: "web-scheduling"})}
            successLabel={t("Email sent!", {ns: "web-scheduling"})}
            className="w-full"
            resetTimeout={20000}
            size={ButtonSize.LG}
            onClick={sendEmail}
          />
          {unauthedPatient?.anonymizedPhoneNumber && sendSms ? (
            <AsyncButton
              label={t("Text it to {{phoneNumber}}", {
                ns: "web-scheduling",
                phoneNumber: unauthedPatient?.anonymizedPhoneNumber,
              })}
              loadingLabel={t("Sending SMS", {ns: "web-scheduling"})}
              successLabel={t("SMS sent!", {ns: "web-scheduling"})}
              className="w-full"
              resetTimeout={20000}
              size={ButtonSize.LG}
              onClick={sendSms}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default React.memo(TokenSent);
